/**
 * What shades a Sketch, drawn by the installer (pure, tested in shade.test.ts): trees, roof obstacles (chimneys,
 * vents, skylights as boxes or cylinders) and neighbouring buildings and walls, in local metres like everything
 * else. The server's shading (app/design/sketch/shading.py) is the truth; this module also gives the editor a quick
 * estimate of each module's solar access while the installer drags (the live heatmap until the preview answers),
 * and the shadows at a chosen sun time for the 2D view.
 *
 * Keep in step with the server: TREE_DEFAULTS (editor/obstacles.py), `crownBase`, the transmission table, the
 * crown shapes (round: an ellipsoid between its base and top; conical: a cone with its apex at the top).
 */
import type { S } from "../api/client";
import type { MessageKey } from "../i18n/keys";
import { newKey, type ObstacleItem } from "../editor/model.ts";
import type { Vec2, Vec3 } from "../editor/geo.ts";
import { dayOfYear, sunAtSolarTime, type SunPosition } from "../editor/layers/sun.ts";

export type ShadeKind = "tree" | "building" | "wall";
export type Crown = "round" | "conical";
export type Leaf = "deciduous" | "evergreen";
export type ObstacleShape = "box" | "cylinder";

export interface ShadeItem {
  key: string;
  id: string | null;
  kind: ShadeKind;
  label?: string | null;
  /** Trees: the trunk, and the crown's radius. */
  center?: Vec2;
  radius?: number;
  /** Buildings and walls: the footprint. */
  polygon?: Vec2[];
  /** Top above the ground (m). */
  height: number;
  /** Trees: where the crown starts (null: a third of the height, at most 3 m). */
  crownBase: number | null;
  crown: Crown;
  leaf: Leaf;
}

export const TREE_DEFAULTS = { radius: 3, height: 10, crown: "round" as Crown, leaf: "deciduous" as Leaf };
export const BUILDING_HEIGHT = 6;
export const WALL_HEIGHT = 2;
export const WALL_THICKNESS = 0.25;
export const MIN_RADIUS = 0.2;
export const MAX_RADIUS = 30;
export const MAX_HEIGHT = 120;
/** Obstacle presets: a click places one this size; height above the roof as the server's defaults. */
export const OBSTACLE_PRESETS: Record<"chimney" | "vent" | "skylight" | "unknown", { height: number; size: number; shape: ObstacleShape; label: MessageKey }> = {
  chimney: { height: 1.0, size: 0.7, shape: "box", label: "sketch.obstacle.chimney" },
  vent: { height: 0.3, size: 0.25, shape: "cylinder", label: "sketch.obstacle.vent" },
  skylight: { height: 0.1, size: 1.0, shape: "box", label: "sketch.obstacle.skylight" },
  unknown: { height: 0.5, size: 0.6, shape: "box", label: "sketch.obstacle.other" },
};
export type ObstaclePreset = keyof typeof OBSTACLE_PRESETS;
/** The share of the beam a crown lets through by month (January first), northern hemisphere. */
export const DECIDUOUS_TRANSMISSION = [0.6, 0.6, 0.55, 0.4, 0.25, 0.2, 0.2, 0.2, 0.25, 0.4, 0.55, 0.6];
export const EVERGREEN_TRANSMISSION = 0.15;
const RAD = Math.PI / 180;

export const crownBase = (item: Pick<ShadeItem, "crownBase" | "height">) =>
  Math.max(0, Math.min(item.height - 0.1, item.crownBase ?? Math.min(3, item.height / 3)));

export function transmission(leaf: Leaf, month: number, lat: number): number {
  if (leaf === "evergreen") return EVERGREEN_TRANSMISSION;
  return DECIDUOUS_TRANSMISSION[(month + (lat < 0 ? 6 : 0)) % 12]!;
}

// ------------------------------------------------------------------------------------------- the API's documents
export function shadeFrom(doc: Pick<S["EditorDocument"], "shade_objects">): ShadeItem[] {
  return (doc.shade_objects ?? []).map((o) => ({
    key: newKey("t"),
    id: o.id,
    kind: o.kind,
    label: o.label ?? null,
    center: o.center as Vec2 | undefined,
    radius: o.radius_m,
    polygon: o.polygon as Vec2[] | undefined,
    height: o.height_m,
    crownBase: o.crown_base_m ?? null,
    crown: (o.crown as Crown | undefined) ?? TREE_DEFAULTS.crown,
    leaf: (o.leaf as Leaf | undefined) ?? TREE_DEFAULTS.leaf,
  }));
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;
const r2 = (v: number) => Math.round(v * 100) / 100;

export function editorShade(items: ShadeItem[]): S["EditorShadeObject"][] {
  return items.map((s) =>
    s.kind === "tree"
      ? {
          id: s.id,
          kind: "tree",
          label: s.label ?? null,
          center: [r3(s.center![0]), r3(s.center![1])],
          radius_m: r2(s.radius ?? TREE_DEFAULTS.radius),
          height_m: r2(s.height),
          crown_base_m: s.crownBase == null ? null : r2(s.crownBase),
          crown: s.crown,
          leaf: s.leaf,
        }
      : { id: s.id, kind: s.kind, label: s.label ?? null, polygon: s.polygon!.map(([x, y]) => [r3(x), r3(y)]), height_m: r2(s.height) },
  );
}

// ------------------------------------------------------------------------------------------- making and editing
export function treeAt(center: Vec2, radius = TREE_DEFAULTS.radius, o: Partial<Pick<ShadeItem, "height" | "crown" | "leaf">> = {}): ShadeItem {
  return {
    key: newKey("t"),
    id: null,
    kind: "tree",
    center,
    radius: clamp(radius, MIN_RADIUS, MAX_RADIUS),
    height: o.height ?? TREE_DEFAULTS.height,
    crownBase: null,
    crown: o.crown ?? TREE_DEFAULTS.crown,
    leaf: o.leaf ?? TREE_DEFAULTS.leaf,
  };
}

export function buildingOf(corners: Vec2[], height = BUILDING_HEIGHT): ShadeItem {
  return { key: newKey("t"), id: null, kind: "building", polygon: ccw(corners), height, crownBase: null, crown: "round", leaf: "deciduous" };
}

/** A wall between two points: a thin footprint `WALL_THICKNESS` wide, centred on the line. */
export function wallBetween(a: Vec2, b: Vec2, height = WALL_HEIGHT): ShadeItem | null {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (len < 0.2) return null;
  const nx = (-(b[1] - a[1]) / len) * (WALL_THICKNESS / 2);
  const ny = ((b[0] - a[0]) / len) * (WALL_THICKNESS / 2);
  const polygon: Vec2[] = [[a[0] - nx, a[1] - ny], [b[0] - nx, b[1] - ny], [b[0] + nx, b[1] + ny], [a[0] + nx, a[1] + ny]];
  return { key: newKey("t"), id: null, kind: "wall", polygon: ccw(polygon), height, crownBase: null, crown: "round", leaf: "deciduous" };
}

/** A box obstacle from one corner to the opposite one, its sides along `angleDeg` (the house's direction). */
export function obstacleBox(kind: ObstaclePreset, a: Vec2, b: Vec2, angleDeg = 0): ObstacleItem {
  const c = Math.cos(angleDeg * RAD), s = Math.sin(angleDeg * RAD);
  // b in the box's frame (u along the house)
  const du = (b[0] - a[0]) * c + (b[1] - a[1]) * s;
  const dv = -(b[0] - a[0]) * s + (b[1] - a[1]) * c;
  const at = (u: number, v: number): Vec2 => [a[0] + u * c - v * s, a[1] + u * s + v * c];
  const polygon = ccw([at(0, 0), at(du, 0), at(du, dv), at(0, dv)]);
  const preset = OBSTACLE_PRESETS[kind];
  return { key: newKey("o"), id: null, kind, polygon, height_m: preset.height, source: "manual", confidence: 1, shape: "box" };
}

/** A preset-sized obstacle centred on a click (a box along the house, or a round one). */
export function obstacleAt(kind: ObstaclePreset, center: Vec2, shape: ObstacleShape = OBSTACLE_PRESETS[kind].shape, angleDeg = 0, size = OBSTACLE_PRESETS[kind].size): ObstacleItem {
  if (shape === "cylinder") return obstacleCylinder(kind, center, size / 2);
  const c = Math.cos(angleDeg * RAD), s = Math.sin(angleDeg * RAD);
  const h = size / 2;
  const corner: Vec2 = [center[0] - h * c + h * s, center[1] - h * s - h * c];
  return obstacleBox(kind, corner, [center[0] + h * c - h * s, center[1] + h * s + h * c], angleDeg);
}

export function obstacleCylinder(kind: ObstaclePreset, center: Vec2, radius: number): ObstacleItem {
  const r = clamp(radius, 0.05, 5);
  const polygon = Array.from({ length: 16 }, (_, i) => [center[0] + r * Math.cos((2 * Math.PI * i) / 16), center[1] + r * Math.sin((2 * Math.PI * i) / 16)] as Vec2);
  return { key: newKey("o"), id: null, kind, polygon, height_m: OBSTACLE_PRESETS[kind].height, source: "manual", confidence: 1, shape: "cylinder" };
}

export function shapeOf(o: ObstacleItem): ObstacleShape {
  return o.shape ?? (o.polygon.length >= 12 ? "cylinder" : "box");
}

export function moveShade(item: ShadeItem, d: Vec2): ShadeItem {
  return item.kind === "tree"
    ? { ...item, center: [item.center![0] + d[0], item.center![1] + d[1]] }
    : { ...item, polygon: item.polygon!.map(([x, y]) => [x + d[0], y + d[1]] as Vec2) };
}

export function moveObstacle(o: ObstacleItem, d: Vec2): ObstacleItem {
  return { ...o, polygon: o.polygon.map(([x, y]) => [x + d[0], y + d[1]] as Vec2) };
}

export const setHeight = (item: ShadeItem, h: number): ShadeItem => {
  const height = clamp(h, 0.5, MAX_HEIGHT);
  return { ...item, height, crownBase: item.crownBase == null ? null : Math.min(item.crownBase, height - 0.5) };
};
export const setRadius = (item: ShadeItem, r: number): ShadeItem => ({ ...item, radius: clamp(r, MIN_RADIUS, MAX_RADIUS) });

/** A round obstacle's centre and radius; a box's centre and half-diagonal. */
export function obstacleCircle(o: ObstacleItem): { center: Vec2; radius: number } {
  const c = centroid(o.polygon);
  return { center: c, radius: Math.max(...o.polygon.map((p) => Math.hypot(p[0] - c[0], p[1] - c[1]))) };
}

export function resizeCylinder(o: ObstacleItem, radius: number): ObstacleItem {
  const { center } = obstacleCircle(o);
  return { ...obstacleCylinder(o.kind as ObstaclePreset, center, radius), key: o.key, id: o.id, kind: o.kind, height_m: o.height_m };
}

// ------------------------------------------------------------------------------------------- hit tests
export function shadeAt(items: ShadeItem[], q: Vec2, tol: number): string | null {
  for (const s of [...items].reverse()) {
    if (s.kind === "tree" && Math.hypot(q[0] - s.center![0], q[1] - s.center![1]) <= (s.radius ?? 0) + tol) return s.key;
    if (s.kind !== "tree" && s.polygon && (inside(q, s.polygon) || distToRing(q, s.polygon) <= tol)) return s.key;
  }
  return null;
}

export function obstacleHit(obstacles: ObstacleItem[], q: Vec2, tol: number): string | null {
  for (const o of [...obstacles].reverse()) if (inside(q, o.polygon) || distToRing(q, o.polygon) <= tol) return o.key;
  return null;
}

// ------------------------------------------------------------------------------------------- the sun and shadows
/** The sun on the 21st of a month (0 = January) at a solar hour. */
export function sunAt(lat: number, month: number, solarHour: number): SunPosition {
  return sunAtSolarTime(lat, dayOfYear(month, 21), solarHour * 60);
}

/** The outline a shade source casts on level ground (z = `ground`) with the sun at `sun`; null below the horizon. */
export function groundShadow(item: ShadeItem, sun: SunPosition, ground = 0): Vec2[] | null {
  if (sun.elevation <= 0.5) return null;
  const pts: Vec3[] = [];
  if (item.kind === "tree") {
    const base = crownBase(item);
    const r = item.radius ?? TREE_DEFAULTS.radius;
    const [cx, cy] = item.center!;
    for (let k = 0; k <= 8; k++) {
      const z = base + ((item.height - base) * k) / 8;
      const rz = crownRadius(item, z);
      for (let i = 0; i < 16; i++) pts.push([cx + rz * Math.cos((i * Math.PI) / 8), cy + rz * Math.sin((i * Math.PI) / 8), z]);
    }
    pts.push([cx, cy, 0], [cx + r * 0.08, cy, base]); // the trunk
  } else {
    for (const [x, y] of item.polygon!) pts.push([x, y, 0], [x, y, item.height]);
  }
  return convexHull(pts.map((p) => project(p, sun, ground)));
}

/** Where a point's shadow falls on the level `ground`. */
export function project([x, y, z]: Vec3, sun: SunPosition, ground = 0): Vec2 {
  const k = Math.max(0, z - ground) / Math.tan(Math.max(sun.elevation, 0.5) * RAD);
  return [x - Math.sin(sun.azimuth * RAD) * k, y - Math.cos(sun.azimuth * RAD) * k];
}

/** The crown's radius at height z (negative outside it). */
export function crownRadius(item: ShadeItem, z: number): number {
  const base = crownBase(item);
  const h = Math.max(item.height - base, 1e-6);
  const k = (z - base) / h;
  if (k < 0 || k > 1) return -1;
  const r = item.radius ?? TREE_DEFAULTS.radius;
  return item.crown === "conical" ? r * (1 - k) : r * Math.sqrt(Math.max(0, 1 - (2 * k - 1) ** 2));
}

// ------------------------------------------------------------------------------------------- the live estimate
export interface Blocker {
  /** A vertical prism: footprint and z range (roof obstacles stand on the roof). */
  ring: Vec2[];
  zLo: number;
  zHi: number;
}

export interface ShadeScene {
  trees: ShadeItem[];
  blockers: Blocker[];
}

export function sceneOf(shade: ShadeItem[], obstacles: { polygon: Vec2[]; height_m: number | null; base: number }[]): ShadeScene {
  return {
    trees: shade.filter((s) => s.kind === "tree"),
    blockers: [
      ...shade.filter((s) => s.kind !== "tree" && s.polygon).map((s) => ({ ring: s.polygon!, zLo: 0, zHi: s.height })),
      ...obstacles.filter((o) => (o.height_m ?? 0) >= 0.05).map((o) => ({ ring: o.polygon, zLo: o.base - 0.3, zHi: o.base + (o.height_m ?? 0) })),
    ],
  };
}

/** How much of a ray from `p` towards `dir` gets through (0 opaque … 1 clear), in a month. */
export function rayThrough(scene: ShadeScene, p: Vec3, dir: Vec3, month: number, lat: number): number {
  const h = Math.hypot(dir[0], dir[1]);
  if (h < 1e-9) return 1;
  const ux = dir[0] / h, uy = dir[1] / h, slope = dir[2] / h;
  for (const b of scene.blockers) if (p[2] < b.zHi && hitsPrism(p, ux, uy, slope, b)) return 0;
  let t = 1;
  for (const tree of scene.trees) {
    const [cx, cy] = tree.center!;
    const r = tree.radius ?? TREE_DEFAULTS.radius;
    const rx = cx - p[0], ry = cy - p[1];
    const along = rx * ux + ry * uy;
    const miss = Math.abs(rx * uy - ry * ux);
    if (miss > r || along < -r) continue;
    const half = Math.sqrt(r * r - miss * miss);
    const base = crownBase(tree);
    const lo = Math.max(Math.max(0, along - half), (base - p[2]) / slope);
    const hi = Math.min(along + half, (tree.height - p[2]) / slope);
    if (!(hi > lo)) continue;
    for (let k = 0; k < 10; k++) {
      const d = lo + ((hi - lo) * (k + 0.5)) / 10;
      const x = p[0] + ux * d, y = p[1] + uy * d, z = p[2] + slope * d;
      if (Math.hypot(x - cx, y - cy) <= crownRadius(tree, z)) {
        t *= transmission(tree.leaf, month, lat);
        break;
      }
    }
  }
  return t;
}

function hitsPrism(p: Vec3, ux: number, uy: number, slope: number, b: Blocker): boolean {
  const ring = b.ring;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!, c = ring[(i + 1) % ring.length]!;
    const ex = c[0] - a[0], ey = c[1] - a[1];
    const den = ux * ey - uy * ex;
    if (Math.abs(den) < 1e-12) continue;
    const qx = p[0] - a[0], qy = p[1] - a[1];
    const t = (qy * ex - qx * ey) / den;
    const s = (qy * ux - qx * uy) / den;
    if (t <= 1e-6 || s < 0 || s > 1) continue;
    const z = p[2] + t * slope;
    if (z >= b.zLo && z <= b.zHi) return true;
  }
  return false;
}

interface SkyPoint {
  dir: Vec3;
  month: number;
  beam: number; // W/m² × hours (normal incidence)
}

const ASHRAE_A = [1230, 1215, 1186, 1136, 1104, 1088, 1085, 1107, 1151, 1192, 1221, 1233];
const ASHRAE_B = [0.142, 0.144, 0.156, 0.18, 0.196, 0.205, 0.207, 0.201, 0.177, 0.16, 0.149, 0.142];
const ASHRAE_C = [0.058, 0.06, 0.071, 0.097, 0.121, 0.134, 0.136, 0.122, 0.092, 0.073, 0.063, 0.057];
const DAYS = [31, 28.25, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const skyCache = new Map<number, { sun: SkyPoint[]; diffuse: number[] }>();

/** The typical sky (as the server's provisional estimate): the sun every half hour on the 21st of each month. */
export function typicalSky(lat: number): { sun: SkyPoint[]; diffuse: number[] } {
  const key = Math.round(lat * 10);
  const hit = skyCache.get(key);
  if (hit) return hit;
  const sun: SkyPoint[] = [];
  const diffuse = new Array(12).fill(0);
  for (let m = 0; m < 12; m++) {
    for (let minutes = 15; minutes < 1440; minutes += 30) {
      const p = sunAtSolarTime(lat, dayOfYear(m, 21), minutes);
      if (p.elevation <= 0.5) continue;
      const sinH = Math.sin(p.elevation * RAD);
      const dni = ASHRAE_A[m]! * Math.exp(-ASHRAE_B[m]! / sinH);
      const hours = DAYS[m]! * 0.5;
      const e = p.elevation * RAD, a = p.azimuth * RAD;
      sun.push({ dir: [Math.sin(a) * Math.cos(e), Math.cos(a) * Math.cos(e), Math.sin(e)], month: m, beam: 0.55 * dni * hours });
      diffuse[m] += (ASHRAE_C[m]! * dni + 0.2 * dni * sinH) * hours;
    }
  }
  const out = { sun, diffuse };
  skyCache.set(key, out);
  return out;
}

const DOME: Vec3[] = (() => {
  const out: Vec3[] = [];
  for (let el = 7.5; el < 90; el += 15)
    for (let az = 7.5; az < 360; az += 15) {
      const e = el * RAD, a = az * RAD;
      out.push([Math.sin(a) * Math.cos(e), Math.cos(a) * Math.cos(e), Math.sin(e)]);
    }
  return out;
})();

/**
 * A quick estimate of each point's solar access (shaded ÷ unshaded on its plane, 0–1) under the latitude's typical
 * sky: the beam blocked by the drawn trees, obstacles and buildings, the diffuse sky by the share they hide. The
 * roof's own shade and the horizon are left to the server; its answer replaces this one.
 */
export function liveAccess(points: { xyz: Vec3; normal: Vec3 }[], scene: ShadeScene, lat: number): number[] {
  const sky = typicalSky(lat);
  return points.map(({ xyz, normal }) => {
    let total = 0;
    let open = 0;
    for (const s of sky.sun) {
      const cos = s.dir[0] * normal[0] + s.dir[1] * normal[1] + s.dir[2] * normal[2];
      if (cos <= 0) continue;
      const w = s.beam * cos;
      total += w;
      open += w * rayThrough(scene, xyz, s.dir, s.month, lat);
    }
    let view = 0;
    let seen = 0;
    for (const d of DOME) {
      const cos = d[0] * normal[0] + d[1] * normal[1] + d[2] * normal[2];
      if (cos <= 0) continue;
      const w = cos * Math.cos(Math.asin(d[2]));
      view += w;
      seen += w * rayThrough(scene, xyz, d, 6, lat) * 0.5 + w * rayThrough(scene, xyz, d, 0, lat) * 0.5;
    }
    const tilt = Math.acos(Math.max(-1, Math.min(1, normal[2])));
    const diffuse = sky.diffuse.reduce((a, b) => a + b, 0) * (1 + Math.cos(tilt)) / 2;
    const all = total + diffuse;
    const got = open + (view > 0 ? diffuse * (seen / view) : diffuse);
    return all > 0 ? Math.max(0, Math.min(1, got / all)) : 1;
  });
}

// ------------------------------------------------------------------------------------------- the heatmap
/** Solar access → colour (sketch.css `--sk-heat-*`): violet in deep shade, through magenta and orange, to sun. */
export const HEAT_STOPS: [number, string][] = [
  [0.5, "#4c2fb8"],
  [0.7, "#c2338f"],
  [0.85, "#ff7a3d"],
  [0.95, "#ffc53d"],
  [1.0, "#fff06a"],
];

export function heatColor(access: number): string {
  const v = Math.max(HEAT_STOPS[0]![0], Math.min(1, access));
  for (let i = 1; i < HEAT_STOPS.length; i++) {
    const [b, cb] = HEAT_STOPS[i]!;
    const [a, ca] = HEAT_STOPS[i - 1]!;
    if (v <= b) {
      const t = (v - a) / (b - a || 1);
      return mixHex(ca, cb, t);
    }
  }
  return HEAT_STOPS[HEAT_STOPS.length - 1]![1];
}

function mixHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${pa.map((v, i) => Math.round(v + (pb[i]! - v) * t).toString(16).padStart(2, "0")).join("")}`;
}

// ------------------------------------------------------------------------------------------- geometry helpers
export function centroid(ring: Vec2[]): Vec2 {
  return [ring.reduce((s, p) => s + p[0], 0) / ring.length, ring.reduce((s, p) => s + p[1], 0) / ring.length];
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function area(ring: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i]!, q = ring[(i + 1) % ring.length]!;
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

export const ringArea = (ring: Vec2[]) => Math.abs(area(ring));
const ccw = (ring: Vec2[]): Vec2[] => (area(ring) < 0 ? [...ring].reverse() : ring);

function inside([x, y]: Vec2, ring: Vec2[]): boolean {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

function distToRing(q: Vec2, ring: Vec2[]): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!, b = ring[(i + 1) % ring.length]!;
    const ex = b[0] - a[0], ey = b[1] - a[1];
    const l2 = ex * ex + ey * ey || 1;
    const t = Math.max(0, Math.min(1, ((q[0] - a[0]) * ex + (q[1] - a[1]) * ey) / l2));
    best = Math.min(best, Math.hypot(q[0] - a[0] - t * ex, q[1] - a[1] - t * ey));
  }
  return best;
}

export function convexHull(points: Vec2[]): Vec2[] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts;
  const cross = (o: Vec2, a: Vec2, b: Vec2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Vec2[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Vec2[] = [];
  for (const p of [...pts].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop();
    upper.push(p);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}
